import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Handshake, Plus, Upload, FileText, Sparkles, CheckCircle2, X, Copy, Clock, AlertTriangle,
  ShoppingCart, Trash2, ChevronDown, Ban, Inbox, UserPlus,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Field, Input, Select, Pill, Spinner } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { useBudgetCodes, shortCode } from "./QuoteBudgetStrip";

// =============================================================================
// SupplierQuotesCard — the supplier quote register (mig 474), 28 Oct 2026
//
// James: "add RFQs to subbies and load quotes that I can either one line or
// breakdown into our quote … we should keep them as a register anyway and
// helps the POs and invoices down the line cleanly."
//
// One card, two homes: the fixed-price quote (quoteId + sections → their
// price can be put on our quote) and the project Budget tab (projectId → the
// register after the win, RFQs raised straight off the job).
//
//   Ask for a price   → a requested row; the RFQ text is drafted for you and
//                       copied to the clipboard (email send comes with the
//                       Resend domain)
//   Load their quote  → drop the PDF, Claude reads it (29 Oct 2026, the
//                       supplier QUOTE reader — see SupplierQuoteLoadModal):
//                       their lines at the level THEY summarised them, their
//                       words or tidied, the breakdown as the second line,
//                       their exclusions checked against ours. Onto our quote
//                       as their lines, ONE line, the full breakdown, or onto a
//                       line we already have. The PDF is saved on the quote
//                       (bid_documents, follows it to the project).
//   Accept / Decline  → the one we are going with. Raise a PO reads it
//                       (mig 475).
// =============================================================================

export interface RegisterRow {
  supplier_quote_id: string;
  quote_id: string | null;
  project_id: string | null;
  supplier_id: string | null;             // mig 481 — null when they are not on our list yet
  supplier_name_text: string | null;      // mig 481 — the name as they wrote it
  supplier_email_text: string | null;
  supplier_abn_text: string | null;
  is_unlisted: boolean;
  budget_code: string | null;
  budget_code_label: string | null;
  title: string;
  status: "requested" | "received" | "accepted" | "declined" | "withdrawn";
  requested_at: string;
  requested_to_email: string | null;
  request_text: string | null;
  due_at: string | null;
  received_at: string | null;
  supplier_ref: string | null;
  amount_ex: number | null;
  valid_until: string | null;
  document_id: string | null;
  document_file_name: string | null;
  document_file_path: string | null;
  quote_line_ids: string[];
  accepted_at: string | null;
  declined_at: string | null;
  notes: string | null;
  supplier_name: string;
  supplier_short: string;
  supplier_email: string | null;
  supplier_kind: string | null;
  quote_number: string | null;
  quote_title: string | null;
  project_code: string | null;
  project_name: string | null;
  n_lines: number;
  lines_cost_ex: number;
  lines_sell_ex: number;
  n_pos: number;
  po_numbers: string | null;
}

interface SectionLite { section_id: string; section_name: string }
/** a line already on our quote — "price an existing line from it" (28 Oct 2026: "sometimes we need quotes that are not line for line") */
export interface LineLite { quote_line_id: string; description: string; section_name: string; cost: number; sell: number; supplier_quote_id?: string | null }

interface Props {
  quoteId?: string;
  projectId?: string;
  /** the fixed-price quote's sections — when given, a received price can be put onto the quote */
  sections?: SectionLite[];
  /** the lines already on the quote — a received price can set the cost on one of them instead of adding lines */
  lines?: LineLite[];
  canEdit: boolean;
  defaultMarkupPct?: number | null;
  /** what we are pricing — goes into the drafted RFQ text */
  context?: { title?: string | null; site?: string | null; client?: string | null };
  onLinesChanged?: () => void;
  compact?: boolean;
  /** our quote's Assumptions & Exclusions block — their exclusions are checked against it (29 Oct 2026) */
  ourExclusionsText?: string | null;
  /** add bullets to that block */
  onAddExclusions?: (bullets: string[]) => Promise<void> | void;
}

export function useSupplierQuotes(quoteId?: string, projectId?: string) {
  return useQuery({
    queryKey: ["supplier_quotes", quoteId ?? null, projectId ?? null],
    enabled: !!(quoteId || projectId),
    queryFn: async () => {
      let q = (supabase as any).from("v_supplier_quote_register").select("*").order("requested_at", { ascending: false });
      q = quoteId ? q.eq("quote_id", quoteId) : q.eq("project_id", projectId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as RegisterRow[];
    },
  });
}

const STATUS_TONE: Record<RegisterRow["status"], any> = { requested: "warning", received: "default", accepted: "ok", declined: "default", withdrawn: "default" };
const STATUS_LABEL: Record<RegisterRow["status"], string> = { requested: "waiting", received: "price in", accepted: "going with", declined: "declined", withdrawn: "withdrawn" };

export default function SupplierQuotesCard({ quoteId, projectId, sections, lines, canEdit, defaultMarkupPct, context, onLinesChanged, compact, ourExclusionsText, onAddExclusions }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: rows = [], isLoading, error } = useSupplierQuotes(quoteId, projectId);
  const [askOpen, setAskOpen] = useState(false);
  const [engaging, setEngaging] = useState<string | null>(null);
  const [loadRow, setLoadRow] = useState<RegisterRow | "new" | null>(null);
  const [open, setOpen] = useState(!compact);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["supplier_quotes"] });
    onLinesChanged?.();
  };

  const waiting  = rows.filter(r => r.status === "requested");
  const overdue  = waiting.filter(r => r.due_at && r.due_at < new Date().toISOString().slice(0, 10));
  const received = rows.filter(r => r.status === "received");
  const accepted = rows.filter(r => r.status === "accepted");
  const acceptedTotal = accepted.reduce((a, r) => a + Number(r.amount_ex ?? 0), 0);

  async function setStatus(row: RegisterRow, status: RegisterRow["status"]) {
    setErr(null);
    const { error } = await (supabase as any).from("supplier_quotes").update({ status }).eq("supplier_quote_id", row.supplier_quote_id);
    if (error) setErr(error.message); else refresh();
  }
  async function remove(row: RegisterRow) {
    if (!confirm(`Remove "${row.title}" (${row.supplier_short}) from the register? Lines priced from it stay on the quote.`)) return;
    const { error } = await (supabase as any).from("supplier_quotes").delete().eq("supplier_quote_id", row.supplier_quote_id);
    if (error) setErr(error.message); else refresh();
  }
  async function copyRfq(row: RegisterRow) {
    const text = row.request_text ?? draftRfqText({ supplier: row.supplier_short, title: row.title, due: row.due_at, context });
    try { await navigator.clipboard.writeText(text); setErr(null); } catch { setErr("Couldn't copy — select the text in the row and copy it yourself."); }
  }
  // mig 481 — raising the PO is the moment we engage them: an unlisted name becomes a suppliers row
  // (or matches one by ABN / name), then the PO form opens prefilled from the register row
  async function raisePo(row: RegisterRow) {
    setErr(null); setEngaging(row.supplier_quote_id);
    try {
      let supplierId = row.supplier_id;
      if (!supplierId) {
        const { data, error } = await (supabase as any).schema("app").rpc("supplier_quote_engage", { p_supplier_quote_id: row.supplier_quote_id });
        if (error) throw new Error(error.message);
        supplierId = data as string;
        qc.invalidateQueries({ queryKey: ["supplier_quotes"] });
        qc.invalidateQueries({ queryKey: ["suppliers_active_register"] });
      }
      navigate(`/pos/new?project_id=${row.project_id}&supplier_id=${supplierId}&supplier_quote_id=${row.supplier_quote_id}${row.budget_code ? `&budget_code=${encodeURIComponent(row.budget_code)}` : ""}`);
    } catch (e: any) { setErr(`Couldn't set them up as a supplier: ${e?.message ?? e}`); }
    finally { setEngaging(null); }
  }
  async function openPdf(row: RegisterRow) {
    if (!row.document_file_path) return;
    const { data, error } = await supabase.storage.from("bid-documents").createSignedUrl(row.document_file_path, 600);
    if (error || !data?.signedUrl) { setErr(error?.message ?? "Couldn't open the PDF"); return; }
    window.open(data.signedUrl, "_blank");
  }

  return (
    <div className="border border-line rounded-md bg-ink-card">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <Handshake size={14} className="text-accent"/>
        <span className="font-semibold text-[13px]">Subbies &amp; suppliers</span>
        <span className="text-[11px] text-text-muted">
          {rows.length === 0 ? "no prices asked for yet"
            : <>
                {waiting.length > 0 && <span className={overdue.length ? "text-danger" : "text-warn"}>{waiting.length} waiting{overdue.length ? ` · ${overdue.length} overdue` : ""}</span>}
                {waiting.length > 0 && (received.length > 0 || accepted.length > 0) && " · "}
                {received.length > 0 && <span>{received.length} price{received.length === 1 ? "" : "s"} in</span>}
                {received.length > 0 && accepted.length > 0 && " · "}
                {accepted.length > 0 && <span className="text-ok">{accepted.length} going with · {fmtMoney(acceptedTotal)}</span>}
              </>}
        </span>
        <span className="ml-auto"/>
        {canEdit && open && (
          <span className="inline-flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <Button size="sm" variant="secondary" icon={Inbox} onClick={() => setLoadRow("new")} title="A price you already have — drop their PDF, Claude reads it">Load a quote</Button>
            <Button size="sm" icon={Plus} onClick={() => setAskOpen(true)}>Ask for a price</Button>
          </span>
        )}
        <ChevronDown size={13} className={`text-text-muted transition-transform ${open ? "" : "-rotate-90"}`}/>
      </button>

      {open && (
        <div className="border-t border-line">
          {error && <div className="px-3 py-2 text-[12px] text-danger">Register could not load: {(error as Error).message}</div>}
          {err && <div className="px-3 py-2 text-[12px] text-danger flex items-center gap-2"><AlertTriangle size={12}/>{err}</div>}
          {isLoading && <div className="px-3 py-3 text-[12px] text-text-muted">Loading…</div>}
          {!isLoading && rows.length === 0 && (
            <div className="px-3 py-4 text-[12px] text-text-muted">
              Nothing in the register. <span className="text-text">Ask for a price</span> sends a subbie or supplier an RFQ and keeps the ask here;
              <span className="text-text"> Load a quote</span> records a price you already have — drop their PDF and Claude reads it{sections ? ", then it goes on this quote as their lines, one line, or the breakdown" : ""}.
            </div>
          )}
          {rows.map(r => (
            <div key={r.supplier_quote_id} className="px-3 py-2.5 border-t border-line/60 first:border-t-0 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 items-start">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[13px] text-text">{r.supplier_short}</span>
                  <Pill label={STATUS_LABEL[r.status]} tone={STATUS_TONE[r.status]} small />
                  {r.is_unlisted && <span className="text-[10px] text-text-dim" title="Not set up as a supplier — that happens when you raise the PO">not on our list</span>}
                  {r.budget_code && <span className="font-mono text-[10px] text-accent" title={r.budget_code_label ?? r.budget_code}>{shortCode(r.budget_code)}</span>}
                  {r.status === "requested" && r.due_at && (
                    <span className={`inline-flex items-center gap-1 text-[11px] ${r.due_at < new Date().toISOString().slice(0, 10) ? "text-danger" : "text-text-muted"}`}><Clock size={10}/> due {fmtDate(r.due_at)}</span>
                  )}
                  {projectId && !quoteId && r.quote_number && <span className="font-mono text-[10px] text-text-dim">{r.quote_number}</span>}
                </div>
                <div className="text-[12.5px] text-text mt-0.5">{r.title}</div>
                <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-x-3 gap-y-0.5 flex-wrap">
                  {r.status === "requested" && <span>asked {fmtDate(r.requested_at)}{r.requested_to_email ? ` · ${r.requested_to_email}` : ""}</span>}
                  {r.status !== "requested" && r.amount_ex != null && <span className="font-mono text-text">{fmtMoney(r.amount_ex)} ex</span>}
                  {r.supplier_ref && <span className="font-mono">{r.supplier_ref}</span>}
                  {r.received_at && <span>in {fmtDate(r.received_at)}</span>}
                  {r.valid_until && <span>valid to {fmtDate(r.valid_until)}</span>}
                  {r.document_file_path && (
                    <button type="button" onClick={() => openPdf(r)} className="inline-flex items-center gap-1 text-accent hover:underline"><FileText size={10}/>their PDF</button>
                  )}
                  {r.n_lines > 0 && <span>on our quote: {r.n_lines} line{r.n_lines === 1 ? "" : "s"} · <span className="font-mono">{fmtMoney(r.lines_cost_ex)}</span> cost → <span className="font-mono">{fmtMoney(r.lines_sell_ex)}</span> sell</span>}
                  {r.n_pos > 0 && <span className="text-ok">PO {r.po_numbers}</span>}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  {r.status === "requested" && (
                    <>
                      <button type="button" onClick={() => copyRfq(r)} className="text-[11px] text-text-muted hover:text-text inline-flex items-center gap-1 px-1.5 py-1" title="Copy the RFQ text to paste into an email"><Copy size={11}/>RFQ text</button>
                      <Button size="sm" variant="secondary" icon={Upload} onClick={() => setLoadRow(r)}>Load their quote</Button>
                    </>
                  )}
                  {r.status === "received" && (
                    <>
                      <Button size="sm" variant="secondary" icon={Upload} onClick={() => setLoadRow(r)} title="Change the price or put it on the quote">Edit</Button>
                      <Button size="sm" icon={CheckCircle2} onClick={() => setStatus(r, "accepted")}>Go with this</Button>
                      <button type="button" onClick={() => setStatus(r, "declined")} className="text-[11px] text-text-muted hover:text-danger inline-flex items-center gap-1 px-1.5 py-1" title="Not going with this price"><Ban size={11}/>decline</button>
                    </>
                  )}
                  {r.status === "accepted" && (
                    <>
                      {r.project_id && r.n_pos === 0 && (
                        <Button size="sm" icon={ShoppingCart} disabled={engaging === r.supplier_quote_id} onClick={() => raisePo(r)}
                                title={r.is_unlisted ? "Sets them up as a supplier, then raises the PO from this quote" : "Raise the PO from this quote"}>
                          {engaging === r.supplier_quote_id ? "Setting up…" : "Raise a PO"}
                        </Button>
                      )}
                      <button type="button" onClick={() => setStatus(r, "received")} className="text-[11px] text-text-muted hover:text-text px-1.5 py-1" title="Back to a price in, not yet chosen">undo</button>
                    </>
                  )}
                  {(r.status === "declined" || r.status === "withdrawn") && (
                    <button type="button" onClick={() => setStatus(r, "received")} className="text-[11px] text-text-muted hover:text-text px-1.5 py-1">reinstate</button>
                  )}
                  {r.n_pos === 0 && (
                    <button type="button" onClick={() => remove(r)} className="text-text-dim hover:text-danger p-1" title="Remove from the register"><Trash2 size={12}/></button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {askOpen && (
        <AskModal quoteId={quoteId} projectId={projectId} context={context} onClose={() => setAskOpen(false)} onDone={() => { setAskOpen(false); refresh(); }} />
      )}
      {loadRow && (
        <SupplierQuoteLoadModal
          row={loadRow === "new" ? null : loadRow}
          quoteId={quoteId} projectId={projectId} sections={sections} lines={lines} defaultMarkupPct={defaultMarkupPct ?? 20}
          ourExclusionsText={ourExclusionsText} onAddExclusions={onAddExclusions}
          onClose={() => setLoadRow(null)} onDone={() => { setLoadRow(null); refresh(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// shared bits
// -----------------------------------------------------------------------------
function useSuppliers() {
  return useQuery({
    queryKey: ["suppliers_active_register"],
    staleTime: 5 * 60_000,
    queryFn: async () => (await supabase.from("suppliers").select("supplier_id, legal_name, trading_name, supplier_code, primary_contact_email, supplier_kind, abn").eq("is_active", true).order("legal_name")).data ?? [],
  });
}

function draftRfqText({ supplier, title, due, context }: { supplier: string; title: string; due?: string | null; context?: Props["context"] }) {
  const what = [context?.title, context?.site, context?.client].filter(Boolean).join(" · ");
  return [
    `Hi ${supplier} team,`,
    ``,
    `Could you please price the following for us${what ? ` — ${what}` : ""}:`,
    ``,
    `  ${title}`,
    ``,
    `Please quote ex GST, note any exclusions and your validity, and include your quote reference.`,
    due ? `We need this back by ${fmtDate(due)}.` : `As soon as you can would be appreciated.`,
    ``,
    `Thanks,`,
    `Coretel Group`,
  ].join("\n");
}

function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-6">
      <div className={`bg-ink-card border border-line w-full ${wide ? "md:max-w-4xl" : "md:max-w-2xl"} md:rounded-lg max-h-[94vh] overflow-y-auto`}>
        <div className="p-4 border-b border-line flex items-center justify-between sticky top-0 bg-ink-card z-10">
          <h3 className="font-semibold text-[15px]">{title}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
        <div className="p-3 border-t border-line bg-ink-raised flex justify-end gap-2 sticky bottom-0">{footer}</div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ask for a price — the RFQ out
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// WhoPicker — a supplier on our list, or anyone (mig 481, 29 Oct 2026).
// James: "I need to have quotes from random suppliers and subbies until we win
// and I engage them … I also asked to be able to do this with anyone not just
// suppliers." Pick from the list, or leave it and type their name — they are
// set up as a supplier when the PO is raised, not before.
// -----------------------------------------------------------------------------
function WhoPicker({ suppliers, supplierId, setSupplierId, name, setName, disabled, hint, extra }: {
  suppliers: any[]; supplierId: string; setSupplierId: (id: string) => void; name: string; setName: (n: string) => void; disabled?: boolean; hint?: string; extra?: React.ReactNode;
}) {
  return (
    <Field label="Subbie / supplier" hint={hint ?? (!supplierId && !disabled ? "Not on the list? Just type their name — they're set up as a supplier when you raise the PO." : undefined)}>
      <Select value={supplierId} onChange={e => setSupplierId(e.target.value)} disabled={disabled}>
        <option value="">{name.trim() ? `— ${name.trim()} (not on our list) —` : "— pick, or type a name below —"}</option>
        {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.trading_name ?? s.legal_name}{s.supplier_kind ? ` · ${s.supplier_kind.replace(/_/g, " ")}` : ""}</option>)}
      </Select>
      {!supplierId && !disabled && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="their name, as on the quote" />
          {extra}
        </div>
      )}
    </Field>
  );
}

function AskModal({ quoteId, projectId, context, onClose, onDone }: { quoteId?: string; projectId?: string; context?: Props["context"]; onClose: () => void; onDone: () => void }) {
  const user = useRequireAuth();
  const { data: suppliers = [] } = useSuppliers();
  const { data: codes = [] } = useBudgetCodes();
  const [supplierId, setSupplierId] = useState("");
  const [who, setWho] = useState("");          // mig 481 — anyone, by name
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [due, setDue] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [textTouched, setTextTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const supplier = (suppliers as any[]).find(s => s.supplier_id === supplierId);
  const drafted = useMemo(() => draftRfqText({ supplier: supplier?.trading_name ?? supplier?.legal_name ?? who.trim(), title: title || "(what you need priced)", due, context }), [supplier, who, title, due, context]);
  const body = textTouched ? text : drafted;

  function pickSupplier(id: string) {
    setSupplierId(id);
    const s = (suppliers as any[]).find(x => x.supplier_id === id);
    if (s?.primary_contact_email && !email) setEmail(s.primary_contact_email);
    if (s?.supplier_kind && !code) {
      const want = s.supplier_kind === "plant_hire" ? "plant" : s.supplier_kind === "subcontractor" || s.supplier_kind === "labour_hire" || s.supplier_kind === "structure" ? "subcontract" : "materials";
      const hit = codes.find(c => (c.category ?? "").toLowerCase() === want);
      if (hit) setCode(hit.budget_code);
    }
  }

  async function save(copy: boolean) {
    setErr(null);
    if (!supplierId && !who.trim()) { setErr("Pick the subbie or supplier, or type their name."); return; }
    if (!title.trim()) { setErr("Say what you need priced — it's the heading on the register and in the RFQ."); return; }
    setSaving(true);
    const { error } = await (supabase as any).from("supplier_quotes").insert({
      quote_id: quoteId ?? null, project_id: projectId ?? null, supplier_id: supplierId || null,
      supplier_name_text: supplierId ? null : who.trim(), supplier_email_text: supplierId ? null : (email.trim() || null),
      budget_code: code || null, title: title.trim(), status: "requested",
      requested_to_email: email.trim() || null, request_text: body, due_at: due || null,
      requested_by: user?.user_id ?? null, created_by: user?.user_id ?? null,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    if (copy) { try { await navigator.clipboard.writeText(body); } catch { /* fine */ } }
    onDone();
  }

  return (
    <Modal title="Ask for a price" onClose={onClose} footer={<>
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button variant="secondary" icon={Copy} disabled={saving} onClick={() => save(true)} title="Saves the ask and copies the RFQ text — paste it into an email">Save &amp; copy RFQ text</Button>
      <Button icon={CheckCircle2} disabled={saving} onClick={() => save(false)}>{saving ? "Saving…" : "Save the ask"}</Button>
    </>}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <WhoPicker suppliers={suppliers as any[]} supplierId={supplierId} setSupplierId={pickSupplier} name={who} setName={setWho} />
        <Field label="Budget code it lands in" hint="Defaults from the supplier's kind; the PO takes it later.">
          <Select value={code} onChange={e => setCode(e.target.value)}>
            <option value="">— none yet —</option>
            {codes.map(c => <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name ?? ""}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="What you need priced" hint="In your words — the heading on the register and in the email.">
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 80t crane + rigger, 2 days, Muswellbrook swap-out" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Need it back by"><Input type="date" mono value={due} onChange={e => setDue(e.target.value)} /></Field>
        <Field label="Send to" hint="Their quotes email — for your records and the email you send."><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="quotes@…" /></Field>
      </div>
      <Field label="RFQ text" hint="Drafted for you — edit freely. Email send from Ops comes once the sending domain is verified; until then Save & copy and paste it into Outlook.">
        <textarea rows={9} value={body} onChange={e => { setTextTouched(true); setText(e.target.value); }}
          className="w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[12.5px] font-mono outline-none focus:border-accent" />
      </Field>
      {err && <div className="text-[12px] text-danger flex items-center gap-2"><AlertTriangle size={12}/>{err}</div>}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Load their quote — received, and onto our quote as one line or the breakdown
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Load their quote — 29 Oct 2026 rebuilt around the supplier QUOTE reader
// (worker POST /ai/read-supplier-quote).
//
// James, on John's HLG quote landing as 50 breakdown rows: "the reader must
// build lines the way they want … for John I do not want to change a word in
// this quote … sometimes we do want to polish it up". And: "see some other
// examples of quotes we get back in for things like steelwork, geo,
// electrical, plant".
//
// So: drop the PDF → Claude reads it → you see THEIR lines at the level they
// summarised them (the HLG front page, not the 1.01/1.02 rows), each with
// what sits behind it as the second line. Pick the wording — their words
// exactly, or tidied. Pick what lands on our quote: their lines, one line at
// the total, the full breakdown, or a line we already have. Their exclusions
// are checked against our Assumptions & Exclusions block and the gaps can be
// added with one tick. Everything they said (assumptions, conditions, terms,
// lead time) is kept on the register row.
// -----------------------------------------------------------------------------

type Wording = "verbatim" | "polished";
type Place = "none" | "one" | "lines" | "breakdown" | "existing";

interface BreakRow {
  include: boolean;
  description: string;
  detail: string;
  unit: string;
  quantity: number;
  cost: number;          // per unit (rate) — line cost = quantity × cost
  optional: boolean;
  tag?: "psum" | "rate_only" | "optional" | null;
  summary_index?: number;
}
const UNIT_CHOICES = ["each", "hour", "day", "night", "week", "metre", "square_metre", "cubic_metre", "tonne", "lump_sum", "per_site", "kilometre"];

/** what the worker's read_supplier_quote tool returns (workers/api-gateway — 29 Oct 2026) */
export interface SupplierQuoteRead {
  supplier?: { legal_name?: string; trading_name?: string; abn?: string; contact_name?: string; contact_email?: string; contact_phone?: string };
  quote_ref?: string; quote_date?: string; valid_until?: string; their_title?: string; site_or_project_named?: string;
  is_estimate_only?: boolean; gst_treatment?: "ex_gst" | "inc_gst" | "unclear";
  shape?: "summary_with_breakdown" | "itemised" | "lump_sum" | "letter" | "fee_table";
  summary_lines?: Array<{
    description_verbatim: string; description_polished: string; detail?: string;
    quantity?: number; unit?: string; rate_ex?: number; amount_ex?: number; category?: string;
    is_optional?: boolean; is_provisional?: boolean; is_rate_only?: boolean; in_their_total: boolean;
  }>;
  detail_lines?: Array<{ summary_index: number; item_no?: string; description: string; quantity?: number; unit?: string; rate_ex?: number; amount_ex?: number; note?: string }>;
  scope_bullets?: string[]; assumptions?: string[]; exclusions?: string[]; conditions?: string[];
  payment_terms?: string; lead_time?: string;
  totals?: { subtotal_ex?: number; gst?: number; total_inc?: number; summary_lines_sum_ex?: number };
  warnings?: string[]; confidence?: number;
}

const SHAPE_LABEL: Record<NonNullable<SupplierQuoteRead["shape"]>, string> = {
  summary_with_breakdown: "a summary table with a breakdown behind it",
  itemised: "an itemised list (qty × rate)",
  lump_sum: "one price for a scope",
  letter: "a letter with the prices in the text",
  fee_table: "a fee table",
};

/** rough "do we already say this?" — enough of their significant words appear in our block */
function coveredByOurs(theirs: string, ours: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ");
  const o = norm(ours);
  if (!o.trim()) return false;
  const words = norm(theirs).split(/\s+/).filter(w => w.length > 3 && !STOP.has(w));
  if (!words.length) return false;
  const hits = words.filter(w => o.includes(w)).length;
  return hits / words.length >= 0.6;
}
const STOP = new Set(["with", "from", "that", "this", "than", "them", "they", "their", "will", "have", "been", "were", "which", "where", "when", "into", "onto", "upon", "also", "only", "unless", "other", "after", "before", "under", "above", "works", "work", "site", "additional", "including", "include", "included", "allowance", "required"]);

function rowsFromRead(read: SupplierQuoteRead, wording: Wording): BreakRow[] {
  return (read.summary_lines ?? []).map((l, i) => {
    const hasRate = typeof l.rate_ex === "number" && (l.quantity ?? 0) > 0;
    const qty = hasRate ? Number(l.quantity) : 1;
    const cost = hasRate ? Number(l.rate_ex) : Number(l.amount_ex ?? 0);
    const tag: BreakRow["tag"] = l.is_rate_only ? "rate_only" : l.is_optional ? "optional" : l.is_provisional ? "psum" : null;
    return {
      include: !l.is_rate_only,
      description: ((wording === "verbatim" ? l.description_verbatim : l.description_polished) || l.description_verbatim || "").slice(0, 200),
      detail: (l.detail ?? "").slice(0, 400),
      unit: UNIT_CHOICES.includes(l.unit ?? "") ? (l.unit as string) : hasRate ? "each" : "lump_sum",
      quantity: qty,
      cost: round2(cost),
      optional: !!l.is_optional,
      tag,
      summary_index: i,
    };
  });
}

function rowsFromBreakdown(read: SupplierQuoteRead): BreakRow[] {
  return (read.detail_lines ?? []).filter(d => (d.amount_ex ?? 0) > 0 || (d.rate_ex ?? 0) > 0).map(d => {
    const hasRate = typeof d.rate_ex === "number" && (d.quantity ?? 0) > 0;
    return {
      include: true,
      description: `${d.item_no ? `${d.item_no} ` : ""}${d.description}`.slice(0, 200),
      detail: (d.note ?? "").slice(0, 400),
      unit: UNIT_CHOICES.includes(d.unit ?? "") ? (d.unit as string) : hasRate ? "each" : "lump_sum",
      quantity: hasRate ? Number(d.quantity) : 1,
      cost: round2(hasRate ? Number(d.rate_ex) : Number(d.amount_ex ?? 0)),
      optional: false,
      tag: null,
      summary_index: d.summary_index,
    };
  });
}

function registerNotes(read: SupplierQuoteRead): string {
  const parts: string[] = [];
  if (read.their_title) parts.push(`Their title: ${read.their_title}`);
  if (read.shape) parts.push(`Shape: ${SHAPE_LABEL[read.shape]}${read.is_estimate_only ? " — ESTIMATE ONLY" : ""}`);
  if (read.scope_bullets?.length) parts.push(`Scope:\n${read.scope_bullets.map(s => `• ${s}`).join("\n")}`);
  if (read.assumptions?.length) parts.push(`Their assumptions:\n${read.assumptions.map(s => `• ${s}`).join("\n")}`);
  if (read.exclusions?.length) parts.push(`Their exclusions:\n${read.exclusions.map(s => `• ${s}`).join("\n")}`);
  if (read.conditions?.length) parts.push(`Conditions:\n${read.conditions.map(s => `• ${s}`).join("\n")}`);
  if (read.payment_terms) parts.push(`Payment: ${read.payment_terms}`);
  if (read.lead_time) parts.push(`Lead time: ${read.lead_time}`);
  if (read.supplier?.contact_name || read.supplier?.contact_email) parts.push(`Contact: ${[read.supplier?.contact_name, read.supplier?.contact_email, read.supplier?.contact_phone].filter(Boolean).join(" · ")}`);
  return parts.join("\n\n").slice(0, 6000);
}

export function SupplierQuoteLoadModal({ row, quoteId, projectId, sections, lines, defaultMarkupPct, ourExclusionsText, onAddExclusions, initialFile, onClose, onDone }: {
  row: RegisterRow | null; quoteId?: string; projectId?: string; sections?: SectionLite[]; lines?: LineLite[]; defaultMarkupPct: number;
  ourExclusionsText?: string | null; onAddExclusions?: (bullets: string[]) => Promise<void> | void;
  initialFile?: File | null;
  onClose: () => void; onDone: () => void;
}) {
  const user = useRequireAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: suppliers = [] } = useSuppliers();
  const { data: codes = [] } = useBudgetCodes();
  const canPlace = !!quoteId && !!sections && sections.length > 0;

  const [supplierId, setSupplierId] = useState(row?.supplier_id ?? "");
  const [who, setWho] = useState(row?.supplier_name_text ?? "");          // mig 481 — anyone, by name
  const [title, setTitle] = useState(row?.title ?? "");
  const [code, setCode] = useState(row?.budget_code ?? "");
  const [ref, setRef] = useState(row?.supplier_ref ?? "");
  const [amount, setAmount] = useState(row?.amount_ex != null ? String(row.amount_ex) : "");
  const [valid, setValid] = useState(row?.valid_until ?? "");
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [dragOver, setDragOver] = useState(false);
  const [place, setPlace] = useState<Place>(canPlace && (row?.n_lines ?? 0) === 0 ? "one" : "none");
  const [existingId, setExistingId] = useState<string>("");
  const [remarkup, setRemarkup] = useState(false);
  const existing = (lines ?? []).find(l => l.quote_line_id === existingId) ?? null;
  const [sectionId, setSectionId] = useState(sections?.find(s => /subcontract|subbie/i.test(s.section_name))?.section_id ?? sections?.[0]?.section_id ?? "");
  const [markup, setMarkup] = useState(String(defaultMarkupPct));
  const [rows, setRows] = useState<BreakRow[]>([{ include: true, description: "", detail: "", unit: "each", quantity: 1, cost: 0, optional: false }]);
  const [wording, setWording] = useState<Wording>("verbatim");
  const [detailToSecond, setDetailToSecond] = useState(true);
  const [read, setRead] = useState<SupplierQuoteRead | null>(null);
  const [exclPick, setExclPick] = useState<Record<number, boolean>>({});
  const [reading, setReading] = useState(false);
  const [readStage, setReadStage] = useState<"upload" | "read" | null>(null);
  const [readStart, setReadStart] = useState<number>(0);
  const [readNote, setReadNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const autoReadDone = useRef(false);
  const qc = useQueryClient();
  const [addingSupplier, setAddingSupplier] = useState(false);

  // 29 Oct 2026 — James: "I need to have quotes from random suppliers and subbies
  // until we win and I engage them, so this is a blocker at the quote phase."
  // A supplier the read names (or a name typed here) becomes a suppliers row on
  // the spot — legal/trading name, ABN, contact from the PDF, kind guessed from
  // what they priced. supplier_code is left to the table default (never set from
  // app code). They show up in Suppliers like anyone else.
  async function addSupplierFromRead() {
    const r = read?.supplier;
    const legal = (who.trim() || r?.legal_name || r?.trading_name || "").trim();
    if (!legal) { setErr("Type the supplier's name first."); return; }
    setErr(null); setAddingSupplier(true);
    try {
      const cats = (read?.summary_lines ?? []).filter(l => l.in_their_total).map(l => l.category);
      const kind = cats.filter(c => c === "subcontract").length > cats.length / 2 ? "subcontractor"
        : cats.filter(c => c === "plant").length > cats.length / 2 ? "plant_hire" : "general";
      const payload: any = {
        legal_name: legal,
        trading_name: r?.trading_name && r.trading_name.trim() !== legal ? r.trading_name.trim() : null,
        abn: (r?.abn ?? "").replace(/\D/g, "") || null,
        primary_contact_name: r?.contact_name?.trim() || null,
        primary_contact_email: r?.contact_email?.trim().toLowerCase() || null,
        primary_contact_phone: r?.contact_phone?.trim() || null,
        supplier_kind: kind,
        is_active: true,
        notes: `Added from their quote${read?.quote_ref ? ` ${read.quote_ref}` : ""} while quoting (${new Date().toISOString().slice(0, 10)})`,
        created_by: user?.user_id ?? null,
      };
      const { data, error } = await (supabase as any).from("suppliers").insert(payload).select("supplier_id").single();
      if (error) throw new Error(/duplicate|unique/i.test(error.message) ? `A supplier with that name or ABN is already in the list — pick them above.` : error.message);
      await qc.invalidateQueries({ queryKey: ["suppliers_active_register"] });
      setSupplierId(data.supplier_id);
    } catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setAddingSupplier(false); }
  }

  const supplier = (suppliers as any[]).find(s => s.supplier_id === supplierId);
  const supplierShort = supplier?.trading_name ?? supplier?.legal_name ?? who.trim();
  const mk = Number(markup) || 0;
  const included = rows.filter(r => r.include && r.description.trim() && r.cost > 0 && r.quantity > 0);
  const includedTotal = included.reduce((s, r) => s + (r.optional ? 0 : r.quantity * r.cost), 0);
  const optionalTotal = included.reduce((s, r) => s + (r.optional ? r.quantity * r.cost : 0), 0);
  const amt = Number(amount) || 0;
  const oneLineDesc = `${title || "Supply"} — as per ${supplierShort || "supplier"} quote${ref ? ` ${ref}` : ""}${valid ? `, valid to ${fmtDate(valid)}` : ""}`;
  const oneLineDetail = read
    ? (read.scope_bullets?.length ? read.scope_bullets.join("; ") : (read.summary_lines ?? []).filter(l => l.in_their_total).map(l => l.description_verbatim).join("; ")).slice(0, 400)
    : "";

  // their exclusions vs ours
  const theirExcl = read?.exclusions ?? [];
  const exclState = useMemo(() => theirExcl.map((t, i) => {
    const covered = coveredByOurs(t, ourExclusionsText ?? "");
    return { text: t, covered, pick: exclPick[i] ?? !covered };
  }), [theirExcl, ourExclusionsText, exclPick]);
  const exclToAdd = exclState.filter(e => e.pick && !e.covered).map(e => e.text);

  function pickFile(f: File | null) {
    if (!f) return;
    if (!/pdf$/i.test(f.type) && !/\.pdf$/i.test(f.name)) { setErr("That's not a PDF."); return; }
    setErr(null); setFile(f);
    autoReadDone.current = false;
  }

  // a dropped / chosen PDF is read straight away — the read is what fills the form
  useEffect(() => {
    if (file && !read && !reading && !autoReadDone.current) { autoReadDone.current = true; void readWithClaude(file); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // wording toggle re-derives the descriptions (edits to a line are kept where its text is unchanged from the other wording)
  useEffect(() => {
    if (!read || place === "breakdown") return;
    setRows(prev => {
      const fresh = rowsFromRead(read, wording);
      return fresh.map((f, i) => {
        const p = prev[i];
        if (!p || p.summary_index !== f.summary_index) return f;
        const other = wording === "verbatim" ? read.summary_lines?.[i]?.description_polished : read.summary_lines?.[i]?.description_verbatim;
        const descUntouched = p.description === (other ?? "").slice(0, 200);
        return { ...p, description: descUntouched ? f.description : p.description };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wording]);

  async function readWithClaude(f: File) {
    if (!user) return;
    setErr(null); setReading(true); setReadNote(null); setReadStage("upload"); setReadStart(Date.now());
    try {
      const safe = f.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
      const key = `supplier-quote-reads/${user.user_id}/${Date.now()}_${safe}`;
      const sign = await apiFetch("/sign-upload", { method: "POST", body: JSON.stringify({ object_key: key, bucket: "coretel-prod", content_type: "application/pdf" }) });
      if (!sign.ok) throw new Error(`sign-upload failed (${sign.status})`);
      const { upload_url, object_key } = await sign.json() as { upload_url: string; object_key: string };
      const put = await fetch(upload_url, { method: "PUT", body: f, headers: { "content-type": "application/pdf" } });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      setReadStage("read");
      const res = await apiFetch("/ai/read-supplier-quote", { method: "POST", body: JSON.stringify({ object_key }) });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(res.status === 404 ? "The worker doesn't have the quote reader yet — deploy it, then try again." : `Claude couldn't read it (${res.status}) ${t.slice(0, 160)}`);
      }
      const payload = await res.json() as { read: SupplierQuoteRead };
      const r = payload.read ?? ({} as SupplierQuoteRead);
      setRead(r);
      setExclPick({});
      // header fields — only fill what's blank
      if (!ref && r.quote_ref) setRef(String(r.quote_ref).slice(0, 60));
      if (!valid && r.valid_until && /^\d{4}-\d{2}-\d{2}$/.test(r.valid_until)) setValid(r.valid_until);
      if (!title && r.their_title) setTitle(String(r.their_title).slice(0, 120));
      if (typeof r.totals?.subtotal_ex === "number") setAmount(String(round2(r.totals.subtotal_ex)));
      else if (typeof r.totals?.summary_lines_sum_ex === "number") setAmount(String(round2(r.totals.summary_lines_sum_ex)));
      if (!supplierId && r.supplier) {
        const abn = (r.supplier.abn ?? "").replace(/\D/g, "");
        const names = [r.supplier.trading_name, r.supplier.legal_name].filter(Boolean).map(n => String(n).toLowerCase());
        const hit = (suppliers as any[]).find(s =>
          (abn && String(s.abn ?? "").replace(/\D/g, "") === abn)
          || names.some(n => [s.legal_name, s.trading_name].filter(Boolean).some((x: string) => x.toLowerCase().includes(n.split(" ")[0]) || n.includes(x.toLowerCase().split(" ")[0]))));
        if (hit) setSupplierId(hit.supplier_id);
        else if (!who.trim()) setWho(String(r.supplier.trading_name ?? r.supplier.legal_name ?? "").slice(0, 120));
      }
      const sl = r.summary_lines ?? [];
      setRows(sl.length ? rowsFromRead(r, wording) : [{ include: true, description: "", detail: "", unit: "lump_sum", quantity: 1, cost: Number(r.totals?.subtotal_ex ?? 0), optional: false }]);
      if (canPlace) setPlace(sl.length > 1 ? "lines" : "one");
      const n = sl.filter(l => l.in_their_total).length;
      const opt = sl.filter(l => l.is_optional).length;
      const bd = (r.detail_lines ?? []).length;
      setReadNote(
        `Read as ${r.shape ? SHAPE_LABEL[r.shape] : "a quote"}: ${n} priced line${n === 1 ? "" : "s"}${opt ? `, ${opt} optional` : ""}${bd ? `, ${bd} breakdown rows behind them` : ""}`
        + `${r.exclusions?.length ? ` · ${r.exclusions.length} exclusions` : ""}${r.is_estimate_only ? " · ESTIMATE ONLY" : ""}.`,
      );
    } catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setReading(false); setReadStage(null); }
  }

  function switchPlace(p: Place) {
    if (p === "breakdown" && read && (read.detail_lines ?? []).length) setRows(rowsFromBreakdown(read));
    if (p === "lines" && read && place === "breakdown") setRows(rowsFromRead(read, wording));
    setPlace(p);
  }

  async function save() {
    setErr(null);
    if (!supplierId && !who.trim()) { setErr("Pick the subbie or supplier, or type their name."); return; }
    if (!title.trim()) { setErr("Give it a title — what the price is for."); return; }
    const addsLines = place === "lines" || place === "breakdown";
    if (!addsLines && place !== "none" && !(amt > 0)) { setErr("Their price ex GST."); return; }
    if (place === "none" && !(amt > 0) && !read) { setErr("Their price ex GST."); return; }
    if ((place === "one" || addsLines) && !sectionId) { setErr("Pick the section on our quote it goes in."); return; }
    if (place === "existing" && !existingId) { setErr("Pick the line on our quote their price goes against."); return; }
    if (addsLines && !included.length) { setErr("Tick at least one line with a description, a quantity and a cost."); return; }
    setSaving(true);
    try {
      // 1. their PDF, on the quote (or the project) so it follows the job
      let document_id: string | null = row?.document_id ?? null;
      if (file) {
        const safe = file.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
        const path = `${quoteId ? `quote-${quoteId}` : `project-${projectId}`}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from("bid-documents").upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
        if (upErr) throw new Error(`Saving the PDF failed: ${upErr.message}`);
        const { data: doc, error: docErr } = await (supabase as any).from("bid_documents").insert({
          quote_id: quoteId ?? null, project_id: projectId ?? null,
          file_name: file.name, file_path: path, file_size_bytes: file.size, mime_type: file.type || "application/pdf",
          doc_type: "other",
          title: `Supplier quote — ${supplierShort}${ref ? ` · ${ref}` : ""}`,
          description: title.trim(),
          uploaded_by: user?.user_id ?? null,
        }).select("id").single();
        if (docErr) throw new Error(`Recording the PDF failed: ${docErr.message}`);
        document_id = doc.id;
      }

      // 2. the register row — received; everything they said rides along in notes
      const finalAmount = addsLines ? (typeof read?.totals?.subtotal_ex === "number" ? read.totals.subtotal_ex : includedTotal) : amt;
      const patch: any = {
        supplier_id: supplierId || null, title: title.trim(), budget_code: code || null,
        supplier_name_text: supplierId ? null : who.trim(),
        supplier_email_text: supplierId ? null : (read?.supplier?.contact_email?.trim().toLowerCase() || row?.supplier_email_text || null),
        supplier_abn_text: supplierId ? null : ((read?.supplier?.abn ?? row?.supplier_abn_text ?? "").replace(/\D/g, "") || null),
        status: row && row.status === "accepted" ? "accepted" : "received",
        supplier_ref: ref.trim() || null, amount_ex: round2(finalAmount), valid_until: valid || null, document_id,
      };
      if (read) patch.notes = registerNotes(read) || null;
      let sqId = row?.supplier_quote_id ?? null;
      if (sqId) {
        const { error } = await (supabase as any).from("supplier_quotes").update(patch).eq("supplier_quote_id", sqId);
        if (error) throw new Error(error.message);
      } else {
        const { data, error } = await (supabase as any).from("supplier_quotes").insert({ ...patch, quote_id: quoteId ?? null, project_id: projectId ?? null, created_by: user?.user_id ?? null }).select("supplier_quote_id").single();
        if (error) throw new Error(error.message);
        sqId = data.supplier_quote_id;
      }

      // 3a. their price onto a line we already have: cost set, sell kept (or re-marked up), tagged
      if (place === "existing" && quoteId && sqId && existing) {
        const lp: any = { cost_price_ex_gst: amt, supplier_quote_id: sqId, line_source: "subbie_quote" };
        if (code) lp.budget_code = code;
        if (remarkup) { lp.markup_percent = mk; lp.sell_price_ex_gst = round2(amt * (1 + mk / 100)); }
        const { error } = await (supabase as any).from("quote_lines").update(lp).eq("quote_line_id", existingId);
        if (error) throw new Error(`Pricing the line failed: ${error.message}`);
        const ids = [...new Set([...(row?.quote_line_ids ?? []), existingId])];
        await (supabase as any).from("supplier_quotes").update({ quote_line_ids: ids }).eq("supplier_quote_id", sqId);
      }

      // 3b. onto our quote as new lines
      if ((place === "one" || addsLines) && quoteId && sqId) {
        const kindCat = supplier?.supplier_kind === "plant_hire" ? "plant" : supplier?.supplier_kind === "general" ? "materials" : "subcontract";
        const base = { quote_id: quoteId, section_id: sectionId, markup_percent: mk, line_source: "subbie_quote", supplier_quote_id: sqId, budget_code: code || null };
        const catFor = (i?: number) => {
          const c = i != null ? read?.summary_lines?.[i]?.category : undefined;
          return c && ["subcontract", "plant", "materials", "fixed", "lafha", "other"].includes(c) ? c : kindCat;
        };
        const toInsert = place === "one"
          ? [{ ...base, category: kindCat, description: oneLineDesc, description_secondary: detailToSecond && oneLineDetail ? oneLineDetail : null, unit: "lump_sum", quantity: 1, cost_price_ex_gst: amt, sell_price_ex_gst: round2(amt * (1 + mk / 100)) }]
          : included.map(r => ({
              ...base,
              category: catFor(r.summary_index),
              description: r.description.trim(),
              description_secondary: detailToSecond && r.detail.trim() ? r.detail.trim() : null,
              unit: r.unit, quantity: r.quantity,
              cost_price_ex_gst: r.cost, sell_price_ex_gst: round2(r.cost * (1 + mk / 100)),
              is_optional: r.optional,
            }));
        const { data: ins, error } = await (supabase as any).from("quote_lines").insert(toInsert).select("quote_line_id");
        if (error) throw new Error(`Putting it on the quote failed: ${error.message}`);
        const ids = [...(row?.quote_line_ids ?? []), ...((ins ?? []) as any[]).map(x => x.quote_line_id)];
        await (supabase as any).from("supplier_quotes").update({ quote_line_ids: ids }).eq("supplier_quote_id", sqId);
      }

      // 4. their exclusions we don't have → our Assumptions & Exclusions block
      if (exclToAdd.length && onAddExclusions && quoteId) {
        await onAddExclusions(exclToAdd);
      }
      onDone();
    } catch (e: any) { setErr(e?.message ?? String(e)); setSaving(false); }
  }

  const saveLabel = saving ? "Saving…"
    : place === "none" ? "Save to the register"
    : place === "one" ? "Save & add one line"
    : place === "existing" ? "Save & price that line"
    : `Save & add ${included.length} line${included.length === 1 ? "" : "s"}`;

  return (
    <Modal title={row ? `${row.supplier_short} — load their quote` : "Load a supplier quote"} onClose={onClose} wide footer={<>
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button icon={CheckCircle2} disabled={saving || reading} onClick={save}>{saveLabel}{exclToAdd.length && (place !== "none") ? ` · +${exclToAdd.length} exclusion${exclToAdd.length === 1 ? "" : "s"}` : ""}</Button>
    </>}>
      {/* the PDF — drop it, Claude reads it */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] ?? null); }}
        onClick={() => !file && fileRef.current?.click()}
        className={`rounded-md border-2 border-dashed px-3 py-3 flex items-center gap-3 transition-colors ${dragOver ? "border-accent bg-accent/10" : file ? "border-line bg-ink" : "border-line hover:border-accent/60 cursor-pointer"}`}
      >
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={e => pickFile(e.target.files?.[0] ?? null)} />
        {reading ? <Spinner size={18}/> : <FileText size={16} className={file ? "text-ok" : "text-text-muted"}/>}
        <div className="min-w-0 flex-1 text-[12.5px]">
          {reading ? <ReadingProgress stage={readStage ?? "upload"} startedAt={readStart} fileName={file?.name ?? ""} sizeKb={file ? file.size / 1024 : 0} />
            : file ? <span className="text-text">{file.name} <span className="text-text-muted">· {(file.size / 1024).toFixed(0)} KB</span></span>
            : row?.document_file_name ? <span className="text-text-muted">On file: {row.document_file_name}. Drop a PDF here to read it (replaces it).</span>
            : <span className="text-text-muted"><span className="text-text">Drop their quote PDF here</span> or click to choose — Claude reads it and fills this in.</span>}
          {readNote && !reading && <div className="text-[11.5px] text-text-muted mt-0.5">{readNote}</div>}
        </div>
        {file && !reading && (
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            {!read && <Button size="sm" variant="secondary" icon={Sparkles} onClick={() => readWithClaude(file)}>Read it</Button>}
            {read && <Button size="sm" variant="ghost" icon={Sparkles} onClick={() => readWithClaude(file)} title="Read it again">Re-read</Button>}
            <button type="button" className="text-text-dim hover:text-danger p-1" title="Remove the PDF" onClick={() => { setFile(null); setRead(null); setReadNote(null); setRows([{ include: true, description: "", detail: "", unit: "each", quantity: 1, cost: 0, optional: false }]); }}><X size={13}/></button>
          </div>
        )}
      </div>
      {read?.warnings?.length ? (
        <div className="text-[11.5px] text-warn space-y-0.5">
          {read.warnings.slice(0, 5).map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle size={11} className="mt-0.5 shrink-0"/>{w}</div>)}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <WhoPicker suppliers={suppliers as any[]} supplierId={supplierId} setSupplierId={setSupplierId} name={who} setName={setWho} disabled={!!row && !!row.supplier_id}
          hint={!supplierId ? (read?.supplier ? `They read as "${read.supplier.trading_name ?? read.supplier.legal_name}" — not on our list; that's fine, they're set up when you raise the PO.` : undefined) : undefined}
          extra={who.trim() ? <button type="button" disabled={addingSupplier} onClick={addSupplierFromRead} className="shrink-0 inline-flex items-center gap-1 text-[11px] text-text-muted hover:text-text px-1" title="Set them up as a supplier now (name, ABN, contact from the PDF)"><UserPlus size={11}/>{addingSupplier ? "adding…" : "add to suppliers now"}</button> : null}
        />
        <Field label="What it's for"><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 80t crane + rigger, 2 days" /></Field>
        <Field label="Budget code">
          <Select value={code} onChange={e => setCode(e.target.value)}>
            <option value="">— none —</option>
            {codes.map(c => <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name ?? ""}</option>)}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Their quote ref"><Input mono value={ref} onChange={e => setRef(e.target.value)} placeholder="SQ-1234" /></Field>
        <Field label="Their price ex GST" hint={(place === "lines" || place === "breakdown") ? `Lines ticked: ${fmtMoney(includedTotal)}${optionalTotal ? ` + ${fmtMoney(optionalTotal)} optional` : ""}` : read?.totals?.total_inc ? `${fmtMoney(read.totals.total_inc)} inc GST on their PDF` : undefined}>
          <Input type="number" mono value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Valid until"><Input type="date" mono value={valid} onChange={e => setValid(e.target.value)} /></Field>
        <Field label="Their terms" hint={read?.lead_time ? `Lead time: ${read.lead_time}` : undefined}>
          <div className="text-[12px] text-text-muted pt-1.5 truncate" title={read?.payment_terms ?? ""}>{read?.payment_terms ?? (read ? "—" : "read from the PDF")}</div>
        </Field>
      </div>

      {canPlace && (
        <div className="border border-line rounded-md p-3 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Onto our quote</div>
            {read && (place === "lines" || place === "one") && (
              <div className="flex items-center gap-3 text-[12px]">
                <span className="inline-flex rounded border border-line overflow-hidden">
                  <button type="button" onClick={() => setWording("verbatim")} className={`px-2 py-1 ${wording === "verbatim" ? "bg-accent text-ink font-medium" : "text-text-muted hover:text-text"}`} title="Their words, not one changed — John's rule">Their words exactly</button>
                  <button type="button" onClick={() => setWording("polished")} className={`px-2 py-1 border-l border-line ${wording === "polished" ? "bg-accent text-ink font-medium" : "text-text-muted hover:text-text"}`} title="Sentence case, no supplier names, same meaning">Tidy it up</button>
                </span>
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-text-muted"><input type="checkbox" className="accent-accent" checked={detailToSecond} onChange={e => setDetailToSecond(e.target.checked)} /> what's behind each line → second line</label>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap text-[12.5px]">
            {read && (read.summary_lines ?? []).length > 1 && <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" className="accent-accent" checked={place === "lines"} onChange={() => switchPlace("lines")} /> Their lines, as they summarised it ({(read.summary_lines ?? []).filter(l => !l.is_rate_only).length})</label>}
            <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" className="accent-accent" checked={place === "one"} onChange={() => switchPlace("one")} /> One line at their total</label>
            {(!read || (read.detail_lines ?? []).length > 0 || (read.summary_lines ?? []).length <= 1) && (
              <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" className="accent-accent" checked={place === "breakdown"} onChange={() => switchPlace("breakdown")} /> {read && (read.detail_lines ?? []).length ? `The full breakdown (${(read.detail_lines ?? []).length} rows)` : "A breakdown I type in"}</label>
            )}
            {(lines ?? []).length > 0 && <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" className="accent-accent" checked={place === "existing"} onChange={() => switchPlace("existing")} /> Price a line we already have</label>}
            <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" className="accent-accent" checked={place === "none"} onChange={() => switchPlace("none")} /> Register only{(row?.n_lines ?? 0) > 0 ? ` (${row!.n_lines} already on)` : ""}</label>
          </div>

          {place === "existing" && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <Field label="The line on our quote" hint="Its cost becomes their price; the sell stays as quoted unless you re-mark it up.">
                <Select value={existingId} onChange={e => setExistingId(e.target.value)}>
                  <option value="">— pick the line —</option>
                  {(lines ?? []).map(l => <option key={l.quote_line_id} value={l.quote_line_id}>{l.section_name} · {l.description.split("\n")[0].slice(0, 70) || "(no description)"} · cost {fmtMoney(l.cost)} / sell {fmtMoney(l.sell)}{l.supplier_quote_id ? " · already priced from a supplier" : ""}</option>)}
                </Select>
              </Field>
              <label className="inline-flex items-center gap-1.5 text-[12px] cursor-pointer pb-2"><input type="checkbox" className="accent-accent" checked={remarkup} onChange={e => setRemarkup(e.target.checked)} /> re-mark up the sell at <input type="number" value={markup} onChange={e => setMarkup(e.target.value)} className="w-14 bg-ink border border-line rounded px-1 py-0.5 font-mono text-[12px]" />%</label>
              {existing && (
                <div className="md:col-span-2 text-[12px] text-text-muted">
                  Line cost <span className="font-mono">{fmtMoney(existing.cost)}</span> → <span className="font-mono text-text">{fmtMoney(amt)}</span>;
                  sell {remarkup ? <><span className="font-mono">{fmtMoney(existing.sell)}</span> → <span className="font-mono text-text">{fmtMoney(round2(amt * (1 + mk / 100)))}</span></> : <>stays <span className="font-mono text-text">{fmtMoney(existing.sell)}</span></>}
                  {!remarkup && existing.sell > 0 && <> · margin {(((existing.sell - amt) / existing.sell) * 100).toFixed(0)}%</>}
                </div>
              )}
            </div>
          )}

          {(place === "one" || place === "lines" || place === "breakdown") && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Section">
                <Select value={sectionId} onChange={e => setSectionId(e.target.value)}>
                  {sections!.map(s => <option key={s.section_id} value={s.section_id}>{s.section_name}</option>)}
                </Select>
              </Field>
              <Field label="Our markup %"><Input type="number" mono value={markup} onChange={e => setMarkup(e.target.value)} /></Field>
              <div className="col-span-2 self-end text-[12px] text-text-muted">
                {place === "one"
                  ? <>Line: <span className="text-text">{oneLineDesc}</span>{detailToSecond && oneLineDetail ? <span className="block text-[11px] text-text-dim truncate" title={oneLineDetail}>↳ {oneLineDetail}</span> : null} cost <span className="font-mono">{fmtMoney(amt)}</span> → sell <span className="font-mono text-text">{fmtMoney(round2(amt * (1 + mk / 100)))}</span></>
                  : <>{included.length} line{included.length === 1 ? "" : "s"} · cost <span className="font-mono">{fmtMoney(includedTotal)}</span> → sell <span className="font-mono text-text">{fmtMoney(round2(includedTotal * (1 + mk / 100)))}</span>{optionalTotal ? <> · optional <span className="font-mono">{fmtMoney(optionalTotal)}</span> not in the total</> : null}
                     {read && typeof read.totals?.subtotal_ex === "number" && Math.abs(read.totals.subtotal_ex - includedTotal) > 1 && place === "lines" && <span className="text-warn"> · their subtotal is {fmtMoney(read.totals.subtotal_ex)}</span>}</>}
              </div>
            </div>
          )}

          {(place === "lines" || place === "breakdown") && (
            <div className="space-y-1">
              <div className="grid grid-cols-[18px_1fr_96px_64px_104px_96px_24px] gap-1.5 px-0.5 text-[10.5px] uppercase tracking-wider2 text-text-muted">
                <span/><span>line{detailToSecond ? " · what's behind it" : ""}</span><span>unit</span><span>qty</span><span>rate ex</span><span className="text-right">total</span><span/>
              </div>
              {rows.map((r, i) => (
                <div key={i} className={`grid grid-cols-[18px_1fr_96px_64px_104px_96px_24px] gap-1.5 items-start rounded px-0.5 py-0.5 ${r.include ? "" : "opacity-50"}`}>
                  <input type="checkbox" className="accent-accent mt-2" checked={r.include} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))} title={r.include ? "On our quote" : "Left off"} />
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Input value={r.description} placeholder="line" onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                      {r.tag === "optional" && <Pill label="optional" tone="warning" small />}
                      {r.tag === "psum" && <Pill label="PSUM" tone="default" small />}
                      {r.tag === "rate_only" && <Pill label="rate only" tone="default" small />}
                    </div>
                    {detailToSecond && (
                      <input value={r.detail} placeholder="second line (what's behind it)" onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, detail: e.target.value } : x))}
                             className="w-full bg-transparent border-0 border-b border-dashed border-line/70 focus:border-accent outline-none text-[11.5px] text-text-muted px-1 py-0.5" />
                    )}
                  </div>
                  <Select value={r.unit} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))}>
                    {UNIT_CHOICES.map(u => <option key={u} value={u}>{u.replace(/_/g, " ")}</option>)}
                  </Select>
                  <Input type="number" mono value={String(r.quantity)} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, quantity: Number(e.target.value) || 0 } : x))} />
                  <Input type="number" mono value={String(r.cost)} placeholder="rate ex" onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, cost: Number(e.target.value) || 0 } : x))} />
                  <div className={`font-mono text-[12px] text-right pt-2 ${r.optional ? "text-text-dim" : "text-text"}`}>{fmtMoney(round2(r.quantity * r.cost))}</div>
                  <button type="button" onClick={() => setRows(rs => rs.length === 1 ? rs : rs.filter((_, j) => j !== i))} className="text-text-dim hover:text-danger mt-2"><Trash2 size={12}/></button>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-1">
                <Button size="sm" variant="ghost" icon={Plus} onClick={() => setRows(rs => [...rs, { include: true, description: "", detail: "", unit: "each", quantity: 1, cost: 0, optional: false }])}>Line</Button>
                {rows.some(r => r.optional) && <span className="text-[11px] text-text-muted">Optional lines go on as optional (not in our total) — untick the box to leave them off.</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* their exclusions against ours */}
      {read && (theirExcl.length > 0 || (read.assumptions ?? []).length > 0) && (
        <div className="border border-line rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">What they exclude · against our Assumptions &amp; Exclusions</div>
            {exclToAdd.length > 0 && onAddExclusions && quoteId && place !== "none" && <span className="text-[11.5px] text-warn">{exclToAdd.length} not in ours — ticked ones are added when you save</span>}
            {theirExcl.length > 0 && !theirExcl.some((_, i) => !exclState[i].covered) && <span className="text-[11.5px] text-ok">All covered by ours</span>}
          </div>
          {theirExcl.length > 0 && (
            <div className="space-y-0.5">
              {exclState.map((e, i) => (
                <label key={i} className={`flex items-start gap-2 text-[12px] ${e.covered ? "text-text-dim" : "text-text"} ${onAddExclusions && quoteId ? "cursor-pointer" : ""}`}>
                  {onAddExclusions && quoteId
                    ? <input type="checkbox" className="accent-accent mt-0.5" disabled={e.covered} checked={e.pick && !e.covered} onChange={ev => setExclPick(p => ({ ...p, [i]: ev.target.checked }))} />
                    : <span className="w-3"/>}
                  <span className="flex-1">{e.text}</span>
                  <span className="text-[10.5px] shrink-0">{e.covered ? "in ours" : <span className="text-warn">gap</span>}</span>
                </label>
              ))}
            </div>
          )}
          {(read.assumptions ?? []).length > 0 && (
            <details className="text-[12px]">
              <summary className="cursor-pointer text-text-muted">Their assumptions ({read.assumptions!.length}){(read.conditions ?? []).length ? ` · conditions (${read.conditions!.length})` : ""} — kept on the register row</summary>
              <ul className="mt-1 space-y-0.5 list-disc pl-5 text-text-muted">
                {read.assumptions!.map((a, i) => <li key={`a${i}`}>{a}</li>)}
                {(read.conditions ?? []).map((a, i) => <li key={`c${i}`} className="text-warn/90">{a}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {err && <div className="text-[12px] text-danger flex items-center gap-2"><AlertTriangle size={12}/>{err}</div>}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// ReadingProgress — what the worker is doing while we wait (29 Oct 2026).
// James: "how long would this normally take and can we get a loading like we
// did before where it shows what the worker is doing while we wait". The read
// is ONE call to the worker — nothing comes back until it's done — so the
// stages below are paced on the clock from what a quote like HLG's takes
// (upload a second or two, then Sonnet reads the whole PDF, ~45–90 s for
// five pages, longer for a thick breakdown). The bar creeps and never claims
// done; the elapsed clock is real.
// -----------------------------------------------------------------------------
const READ_STAGES: Array<{ at: number; text: string }> = [
  { at: 0,   text: "Sending the PDF to the worker…" },
  { at: 3,   text: "Claude is reading every page — the lines, the breakdown, the small print…" },
  { at: 18,  text: "Working out the level — is there a summary table over a breakdown?" },
  { at: 35,  text: "Writing each line two ways — their words exactly, and tidied…" },
  { at: 50,  text: "Pulling out what's behind each line for the second line…" },
  { at: 65,  text: "Listing their exclusions, assumptions and conditions, one each…" },
  { at: 80,  text: "Tying the lines back to their subtotal…" },
  { at: 100, text: "Still going — a thick breakdown takes longer. Hang on…" },
  { at: 150, text: "Still reading. If it passes three minutes, close this and try again — the PDF is kept." },
];
function ReadingProgress({ stage, startedAt, fileName, sizeKb }: { stage: "upload" | "read"; startedAt: number; fileName: string; sizeKb: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const expected = sizeKb > 900 ? 120 : sizeKb > 300 ? 80 : 50;   // a rough feel for "usually about"
  const pct = stage === "upload" ? Math.min(8, secs * 3) : Math.min(92, 8 + 84 * (1 - Math.exp(-secs / (expected * 0.9))));
  const msg = stage === "upload" ? READ_STAGES[0].text : [...READ_STAGES].reverse().find(x => secs >= x.at)!.text;
  const mm = String(Math.floor(secs / 60)).padStart(1, "0"), ss = String(secs % 60).padStart(2, "0");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-text truncate">Reading <span className="font-medium">{fileName}</span></span>
        <span className="font-mono text-[11px] text-text-muted shrink-0">{mm}:{ss} <span className="text-text-dim">· usually about {expected}s</span></span>
      </div>
      <div className="h-1.5 rounded bg-ink overflow-hidden border border-line/60">
        <div className="h-full bg-accent transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11.5px] text-text-muted flex items-center gap-1.5"><Sparkles size={11} className="text-accent animate-pulse shrink-0"/>{msg}</div>
    </div>
  );
}

function round2(n: number) { return Math.round(n * 100) / 100; }
